import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { wizardClasses } from 'Views/Page/Content/Templates/WizardTemplates/wizardCommonStyles';
import { useHistory } from 'react-router-dom';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import useWizardComplete from 'Views/Page/Hooks/useWizardComplete';
import useEventTrackingLogEvent, {
  categories,
  eventSubTypes,
  eventTypes,
} from 'EventTracking/useEventTrackingLogEvent';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import WizardStandardHeader from 'Components/ComponentLibrary/Components/Uncategorized/WizardStandardHeader';
import {
  FDBodyText, FDBox, FDContentContainer, FDStack,
} from '@facet-wealth/design';
import { onExit } from 'Components/ComponentLibrary/Organisms/SelectInstitution/onExit';
import SearchableFinancialInstitutionList
  from 'Components/ComponentLibrary/Organisms/LinkOrAddAccounts/InstitutionIcons/SearchableFinancialInstitutionList';
import { pick } from 'Helpers/Affordances';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { dataAttrs } from 'Helpers/Testing';
import Typography from 'Components/UI/Typography';

const useStyles = buildUseStyles('SelectInstitutionTemplate', (theme) => ({
  root: {
    ...wizardClasses.root(theme),
    margin: 'unset',
  },
  footer: {
    position: 'fixed',
    zIndex: 1,
    bottom: 0,
    width: '100%',
  },
  title: {
    fontSize: 32,
  },
}));

const SelectInstitutionTemplate = ({
  resource,
}) => {
  const {
    loadPageContentLink,
  } = usePageContentContext();

  const { loadModalCollectionByLink } = useModalContext();

  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles(theme);

  const { handleNext } = useWizardComplete(() => onExit(
    resource,
    (link) => loadPageContentLink(link),
    logStandardizedEvent,
    history,
  ));

  const onClickManualInstitutionAdd = resource.withRelationship(fr.relationships.manuallyAddInstitution.name,
    (rel) => async () => {
      logStandardizedEvent(categories.addAccounts, eventTypes.selectInstitution, eventSubTypes.addInstitutionManually);
      if (rel.meta('modal') === 'true') {
        return loadModalCollectionByLink(rel.link('related').url());
      }

      return loadPageContentLink(rel.link('related'));
    },
    () => null);

  useScrollTopOnMount();

  const startingInstitutions = useMemo(() => resource.attr('financialInstitutions')?.resources()?.list(), [resource]);

  const onClickInstitution = useCallback((financialInstitutionResource) => {
    logStandardizedEvent(categories.addAccounts, eventTypes.selectInstitution, eventSubTypes.institutionSelected, {
      eventProperties: {
        institution: financialInstitutionResource.attr('name'),
      },
    });

    const handleRelationship = pick(financialInstitutionResource, {
      [fr.relationships.linkOrAddAccounts.name]: () => loadPageContentLink(financialInstitutionResource.relationship(fr.relationships.linkOrAddAccounts.name).link('related')),
      [fr.relationships.manualAddAccount.name]: () => loadModalCollectionByLink(financialInstitutionResource.relationship(fr.relationships.manualAddAccount.name).link('related').url()),
    }, null);

    if (handleRelationship) {
      handleRelationship();
    }
  }, [logStandardizedEvent, loadPageContentLink]);

  const onSearch = useCallback((searchTerm) => resource.query(
    'getFinancialInstitutions',
    (link) => {
      // Not doing a .copy() because it's too slow (there are 100 financial institutions in the attributes in the parent of the link).
      // This is safe because we got this link from .query, which constructed a new link.
      // The only way we could be modifying the original resource is if we touched link.parent, but we're not
      const linkWithStepType = link.withQueryParams().like('search', searchTerm);

      return linkWithStepType.get();
    },
    () => { throw new Error('Expected the getFinancialInstitutions query'); },
  ), [resource]);

  // This query has to exist for this page to work - just look at the above onSearch which uses the same query.
  const searchPrompt = resource.meta().query('getFinancialInstitutions').attr('prompt');

  return (
    <Box className={classes.root} {...dataAttrs('select-institution-template', resource)}>
      <WizardStandardHeader
        color={'primary'}
        title={resource.attr('moduleName')}
      />
      <FDStack direction="column" spacing={5}>
        <FDBox maxWidth={600}>
          <FDStack direction="column" spacing={4}>
            <Typography
              className={classes.title}
              variant={'h1'}
              color={'primary'}
              weight={'medium'}
              align={'center'}
            >
              {resource.attr('title')}
            </Typography>
            {resource.attr('subtitle').map((text) => <FDBodyText value={text} align={'center'} color={'textDisabled'} />)}
          </FDStack>
        </FDBox> {/* Ending the FDContentContainer because in the design, the lower portion has a smaller maxWidth */}
        <FDContentContainer maxWidth="xs">
          <SearchableFinancialInstitutionList
            startingInstitutions={startingInstitutions}
            onSearch={onSearch}
            searchPrompt={searchPrompt}
            onClickInstitution={onClickInstitution}
            onClickManualInstitutionAdd={onClickManualInstitutionAdd}
          />
          <FDBox paddingY={5} />{/* The position:fixed footer at the bottom will cover up this much of the institution list, which has to be prevented */}
        </FDContentContainer>
      </FDStack>
      <div className={classes.footer}>
        <StepNavigator onNext={handleNext}>
          Cancel
        </StepNavigator>
      </div>
    </Box>
  );
};

SelectInstitutionTemplate.defaultProps = {
};

SelectInstitutionTemplate.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default SelectInstitutionTemplate;
